import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';

import Button from 'components/StyledButton/StyledButton';

import ResetPasswordDialog from '../../dialogs/ResetPasswordDialog/ResetPasswordDialog';
import logo from '../../logo.png';
import api from '../../utils/api';
import snackbar from '../../utils/snackbar';
import { ButtonContainer, FormContainer, Logo, ProgressPlaceholder, StyledProgress, StyledTextField, Title } from './styled';

const LoginPage = () => {
    const { token } = useParams<{ token: string }>();
    const history = useHistory();
    const { t } = useTranslation();

    const [credentials, setCredentials] = useState({
        password: '',
        passwordConfirm: ''
    });

    const [isLoading, setIsLoading] = useState(false);
    const [isResetPasswordDialogOpen, setIsResetPasswordDialogOpen] = useState(false);

    const handleResetPassword = useCallback(() => {
        if (isLoading) return;

        if (credentials.password.length < 6) {
            return snackbar.error(t('dialogs.change_password.messages.password_too_short'));
        }
        else if (credentials.password !== credentials.passwordConfirm) {
            return snackbar.error(t('dialogs.change_password.messages.password_mismatch'));
        }

        setIsLoading(true);
        api.request('/auth/reset_password/' + token, 'POST', credentials).then(() => {
            snackbar.success(t('pages.reset_password.messages.success'));
            history.push('/login');
        }).finally(() => {
            setIsLoading(false);
        });
    }, [credentials, history, isLoading, t, token]);

    const handleInputChange = useCallback((field, e) => {
        const value = e.target.value;
        setCredentials((currentCredentials) => {
            return {...currentCredentials, [field]: value};
        });
    }, []);

    const handleKeyDown = useCallback((e) => {
        if (e.keyCode === 13) {
            handleResetPassword();
        }
    }, [handleResetPassword]);

    return (
        <>
            <Logo src={logo} alt='logo' />
            <FormContainer>
                <Title variant='h4'>
                    {t('pages.reset_password.title')}
                </Title>

                <StyledTextField
                    label={t('pages.reset_password.attributes.password')}
                    variant='outlined'
                    type='password'
                    onChange={(e) => handleInputChange('password', e)}
                    onKeyDown={handleKeyDown}
                    fullWidth
                    autoFocus
                />

                <StyledTextField
                    label={t('pages.reset_password.attributes.password_confirm')}
                    variant='outlined'
                    type='password'
                    onChange={(e) => handleInputChange('passwordConfirm', e)}
                    onKeyDown={handleKeyDown}
                    fullWidth
                />


                {isLoading ?
                    <StyledProgress /> :
                    <ProgressPlaceholder />
                }

                <ButtonContainer>
                    <Button variant='contained' onClick={handleResetPassword}>
                        {t('pages.reset_password.confirm_button')}
                    </Button>
                </ButtonContainer>

                <ResetPasswordDialog open={isResetPasswordDialogOpen} onClose={() => setIsResetPasswordDialogOpen(false)} />
            </FormContainer>
        </>
    );
};

export default LoginPage;
